import { useState, useEffect, useCallback } from 'react';

export type LocationStatus = 'idle' | 'requesting' | 'success' | 'denied' | 'unavailable' | 'unsupported';

export interface UserLocation {
  latitude: number;
  longitude: number;
}

export const useGeolocation = (autoRequest = true) => {
  const [userLocation, setUserLocation] = useState<UserLocation | null>(null);
  const [locationStatus, setLocationStatus] = useState<LocationStatus>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const showLocationDeniedPrompt = useCallback((message?: string) => {
    setLocationStatus('denied');
    message && setErrorMessage(message);
  }, []);

  const getUserLocation = useCallback(async () => {
    if (!navigator.geolocation) {
      setLocationStatus('unsupported');
      setErrorMessage('Geolocation is not supported by this browser.');
      return;
    }

    setLocationStatus('requesting');
    setErrorMessage('');

    let permissionState = 'prompt';
    try {
      if (navigator.permissions) {
        const permission = await navigator.permissions.query({ name: 'geolocation' as any });
        permissionState = permission.state;
      }
    } catch (e) {
    }

    if (permissionState === 'denied') {
      showLocationDeniedPrompt('Browser location is turned off. Please enable it to continue.');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setUserLocation({ latitude, longitude });
        setLocationStatus('success');
      },
      (error) => {
        switch (error.code) {
          case error.PERMISSION_DENIED:
            showLocationDeniedPrompt('Browser location is turned off. Please enable it to continue.');
            break;

          case error.POSITION_UNAVAILABLE:
            setLocationStatus('unavailable');
            setErrorMessage('Unable to get your location. Please try again.');
            break;

          case error.TIMEOUT:
            setLocationStatus('unavailable');
            setErrorMessage('Location request timed out. Please try again.');
            break;

          default:
            setLocationStatus('unavailable');
            showLocationDeniedPrompt('Unable to get your location. Please try again.');
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 0,
      }
    );
  }, [showLocationDeniedPrompt]);

  const retryLocation = useCallback(() => {
    setLocationStatus('idle');
    getUserLocation();
  }, [getUserLocation]);

  useEffect(() => {
    if (autoRequest) {
      getUserLocation();
    }
  }, [autoRequest, getUserLocation]);

  return {
    userLocation,
    locationStatus,
    errorMessage,
    retryLocation,
    getUserLocation,
  };
};
